// shared/overlay – the layer over one rendered page (contract LY §2). An SVG in the page's points (viewBox) scaled to the
// rendered size, drawing exactly layoutItem()'s operations – the same the server burns, so preview = print. Editable:
// drag, resize at the corner within the item's limits, arrow keys (1 pt, Shift 10 pt), „Zurücksetzen“ back to the
// suggestion; onChange(item) after letting go. A suggestion that is not sure shows „Position prüfen“ until moved.
// Mounted by RimoPreview's per-page `overlay` slot (shared/preview).
import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import type { FontKey } from './metrics.js';
import { isDrawn, layoutItem, resetItem, type Layer, type LayerItem, type PageSize } from './overlay.js';

const FONT: Record<FontKey, { family: string; weight: number }> = {
  body: { family: 'Inter, sans-serif', weight: 400 }, bold: { family: 'Inter, sans-serif', weight: 700 },
  mono: { family: "'JetBrains Mono', monospace", weight: 400 }, monoBold: { family: "'JetBrains Mono', monospace", weight: 600 },
  display: { family: 'Barlow, sans-serif', weight: 700 },
};
export interface LayerViewProps {
  layer: Layer | null;
  /** 1-based page this view covers. */
  page: number;
  /** Page size in points as displayed. */
  pageSize: PageSize;
  /** CSS pixels per point (RimoPreview's scale). */
  scale: number;
  /** The viewer's turn (RimoPreview's rotation): pointer moves are turned back into page points. */
  rotation?: number;
  editable?: boolean;
  /** URLs for `mark` / `image` keys (brand/ files). */
  marks?: Record<string, string>;
  onChange?: (item: LayerItem) => void;
  /** Texts in the person's language. */
  labels?: { reset: string; check: string };
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/** The item kept on the page and in its limits (while dragging; the server checks again with validateLayer). */
export function fit(it: LayerItem, size: PageSize): LayerItem {
  const L = it.limits, w = clamp(it.w, L?.minW ?? 20, Math.min(L?.maxW ?? size.w, size.w)), h = clamp(it.h, L?.minH ?? 12, Math.min(L?.maxH ?? size.h, size.h));
  return { ...it, w, h, x: clamp(it.x, 0, size.w - w), y: clamp(it.y, 0, size.h - h) };
}
/** A pointer move on screen (CSS px) → page points, with the viewer's turn undone (the slot is rotated around the page centre). */
export function toPagePoints(dx: number, dy: number, scale: number, rotation: number) {
  const a = (-rotation * Math.PI) / 180, cos = Math.round(Math.cos(a)), sin = Math.round(Math.sin(a));
  return { x: (dx * cos - dy * sin) / scale, y: (dx * sin + dy * cos) / scale };
}
const onSuggestion = (it: LayerItem) => !!it.suggested && it.suggested.page === it.page && Math.abs(it.suggested.x - it.x) < 0.5 && Math.abs(it.suggested.y - it.y) < 0.5;

export function LayerView({ layer, page, pageSize, scale, rotation = 0, editable = false, marks = {}, onChange, labels = { reset: 'Zurücksetzen', check: 'Position prüfen' } }: LayerViewProps) {
  const [live, setLive] = useState<LayerItem | null>(null), [sel, setSel] = useState<string | null>(null);
  const drag = useRef<{ id: string; mode: 'move' | 'size'; x0: number; y0: number; it: LayerItem } | null>(null);
  const items = (layer?.items ?? []).filter((i) => i.page === page && isDrawn(i)).map((i) => (live && live.id === i.id ? live : i));
  if (!items.length) return null;

  const pt = (e: PointerEvent) => toPagePoints(e.clientX, e.clientY, scale, rotation);
  const down = (e: PointerEvent, it: LayerItem, mode: 'move' | 'size') => {
    if (!editable) return;
    e.stopPropagation(); e.preventDefault(); (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const p = pt(e); drag.current = { id: it.id, mode, x0: p.x, y0: p.y, it }; setSel(it.id);
  };
  const move = (e: PointerEvent) => {
    const d = drag.current; if (!d) return;
    const p = pt(e), dx = p.x - d.x0, dy = p.y - d.y0;
    setLive(fit(d.mode === 'move' ? { ...d.it, x: d.it.x + dx, y: d.it.y + dy } : { ...d.it, w: d.it.w + dx, h: d.it.h + dy }, pageSize));
  };
  const up = () => { const d = drag.current; drag.current = null; if (d && live && live.id === d.id) onChange?.(live); setLive(null); };
  const key = (e: KeyboardEvent, it: LayerItem) => {
    if (!editable) return;
    const s = e.shiftKey ? 10 : 1, k = e.key;
    const dx = k === 'ArrowLeft' ? -s : k === 'ArrowRight' ? s : 0, dy = k === 'ArrowUp' ? -s : k === 'ArrowDown' ? s : 0;
    if (dx || dy) { e.preventDefault(); onChange?.(fit({ ...it, x: it.x + dx, y: it.y + dy }, pageSize)); }
  };

  const box: CSSProperties = { position: 'absolute', inset: 0, width: pageSize.w * scale, height: pageSize.h * scale, pointerEvents: 'none' };
  return (
    <svg data-overlay-page={page} style={box} viewBox={`0 0 ${pageSize.w} ${pageSize.h}`} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      {items.map((it) => {
        const { ops } = layoutItem(it), cx = it.x + it.w / 2, cy = it.y + it.h / 2, on = sel === it.id, check = it.suggested && !it.suggested.sure && onSuggestion(it);
        return (
          <g key={it.id} transform={`rotate(${it.tilt ?? 0} ${cx} ${cy})`} opacity={it.opacity ?? 1}>
            <g transform={`translate(${it.x} ${it.y})`} tabIndex={editable ? 0 : -1} role={editable ? 'button' : undefined} aria-label={it.title ?? it.lines?.[0]?.text ?? it.kind}
              onPointerDown={(e) => down(e, it, 'move')} onKeyDown={(e) => key(e, it)} onFocus={() => setSel(it.id)}
              style={{ pointerEvents: 'all', cursor: editable ? 'move' : 'default', outline: 'none' }}>
              {ops.map((o, i) => o.op === 'rect'
                ? <rect key={i} x={o.x} y={o.y} width={o.w} height={o.h} fill={o.fill ?? 'none'} fillOpacity={0.92} stroke={o.color} strokeWidth={o.width} strokeDasharray={o.dash?.join(' ')} />
                : o.op === 'text'
                  ? <text key={i} x={o.x} y={o.y} fontSize={o.size} fontFamily={FONT[o.font].family} fontWeight={FONT[o.font].weight} fill={o.color} style={{ whiteSpace: 'pre' }}>{o.text}</text>
                  : marks[o.key] ? <image key={i} href={marks[o.key]} x={o.x} y={o.y} width={o.w} height={o.h} /> : null)}
              {editable && on && <>
                <rect x={-2} y={-2} width={it.w + 4} height={it.h + 4} fill="none" stroke="#1375BD" strokeWidth={0.8} strokeDasharray="2 2" />
                <rect x={it.w - 4} y={it.h - 4} width={8} height={8} fill="#1375BD" style={{ cursor: 'nwse-resize' }} onPointerDown={(e) => down(e, it, 'size')} />
                {it.suggested && !onSuggestion(it) && (
                  <g transform={`translate(0 ${-14})`} style={{ cursor: 'pointer' }} onPointerDown={(e) => { e.stopPropagation(); onChange?.(resetItem(it)); }}>
                    <rect width={58} height={11} rx={2} fill="#231F20" />
                    <text x={5} y={8} fontSize={7} fontFamily={FONT.body.family} fill="#FBFAFA">↺ {labels.reset}</text>
                  </g>)}
              </>}
              {check && (
                <g transform={`translate(0 ${it.h + 3})`}>
                  <rect width={64} height={11} rx={2} fill="#E0A340" />
                  <text x={5} y={8} fontSize={7} fontFamily={FONT.bold.family} fontWeight={700} fill="#231F20">{labels.check}</text>
                </g>)}
            </g>
          </g>
        );
      })}
    </svg>
  );
}
